import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'

import {PageContentInterface} from "../page-content-interface"
import PageTitle from "../title/page-title"
import RichTextEditorUniversal from "../../../../../core/gd-components/editor-universal/editor-universal"
import AttachmentsManager from "../../../../../core/gd-components/files/attachments-manager/attachments-manager"
import MentionsManager from "../../../../../core/gd/manager/mentions/mentions-manager"
import AttachmentsCollection from "../../../../../core/model2/attachment/attachments"
import OrganizationSettingsManager from "../../../../../core/gd/manager/organization-settings-manager"
import {AttachFileMode} from "../../../../../core/gd-components/files/const"
import {BUS_APP_CONTROLS_RERENDER} from "../../../../../core/gd/bus/bus-event-type"
import {LexicalEditor} from "lexical"

type Props = {
    uniqKey?            : string

    app                 : PageContentInterface
    pageId              : ID

    isPDFVersion?       : boolean

    triggerSave?        : func
}

// const debug = "PageContent"
const debug = false

export default function PageContent(props: Props) {

    const refTextarea           : any = useRef()
    const refAttachmentManager  : any = useRef()

    const [editor, setEditor] = useState<LexicalEditor | undefined>()

    const showAttachments: boolean  = !gd.isSharedView && OrganizationSettingsManager.isAttachmentsEnabled() && !props.isPDFVersion
    const canEdit: boolean          = !gd.isSharedView && props.app.document.canEdit && !props.isPDFVersion

    const page = props.app.document.pages.getPage(props.pageId)!
    page.prepareAttachments(refAttachmentManager, canEdit)

    const attachmentsCollection: AttachmentsCollection = page.attachmentsCollection // there is only one page so get attachments from the first one
    const attachmentsManagerMode: AttachFileMode = props.app.project.properties.canCreateAttachments ? AttachFileMode.EDIT : AttachFileMode.VIEW

    const content: string = page.content
    const contentRTF: any = page.contentRTF

    const showControlsPanel = props.app.editorLocalSettings.showControls

    // The editor instance is assigned deep inside Lexical (EditorRefPlugin) from a *passive* effect,
    // and as a plain field - it re-renders nothing. Reading it off the ref in a layout effect
    // therefore always caught undefined and was never re-run, so the toolbar stayed dead.
    // Passive effects run children-first, so by the time this one fires the ref is filled in; it
    // has no dependency list on purpose, so a remount (uniqKey) or an unmount is picked up too.
    // setEditor bails out on an unchanged value, so this does not loop.
    useEffect(() => {
        setEditor(refTextarea.current?.editor)
    })

    // The PDF export mounts one more PageContent per page over the same app (pdf-version.tsx), and
    // those are built read-only (canEdit is false for isPDFVersion). Only the interactive one may
    // publish to the app, or exporting replaces app.editor with a non-editable instance and the
    // toolbar - which hides itself on !editor.isEditable() - disappears until the page is reloaded.
    const isAppContent = !props.isPDFVersion

    useLayoutEffect(() => {
        if (!isAppContent) return

        props.app.onAttachmentSelect = onAttachmentSelect
    })

    // the toolbar reads app.editor, so it has to be re-rendered once the instance is there
    useLayoutEffect(() => {
        if (!isAppContent) return

        debug && console.log(debug, "editor changed", editor)

        props.app.editor = editor
        gd.bus.triggerEvent(BUS_APP_CONTROLS_RERENDER)
    }, [editor, isAppContent])

    const onMessageChange = (message: string, messageRTF: any) => {
        debug && console.log(debug, "onMessageChange", message, messageRTF)

        const page = props.app.document.pages.getPage(props.pageId)!

        // do nothing if page content is already the same
        // (happens when editor content is updated from outside)
        if(_.isEqual(page.contentRTF, messageRTF)) return

        page.content    = message
        page.contentRTF = messageRTF

        props.triggerSave?.()

        if (refAttachmentManager.current) {
            refAttachmentManager.current.onMessageRTFChanged(messageRTF)
        }
    }

    const pasteImage = (file: any) => {
        refAttachmentManager.current.pasteImage(file)
    }

    const onAttachmentSelect = (value: any) => {
        refAttachmentManager.current.selectAttachment(value)
    }

    // add attachment(s) to message
    const onAddToMessage = (data: any /*attachment or array of attachments*/) => {
        debug && console.log(debug, "onAddToMessage", data)

        if (Array.isArray(data))    refTextarea.current.addAttachmentsToEditor(data)
        else                        refTextarea.current.addAttachmentToEditor(data)
    }

    const onNewAttachment = (attachment: any) => {
        debug && console.log(debug, "onNewAttachment", attachment)

        props.triggerSave?.()

        // auto add to editor

        // aga - super hitrii hack
        // t.k. v notes attachments vsegda vnutri nam nuzno ukazat' special
        // render mode dlya attachments
        attachment.renderMode           = 'notes-edit'
        attachment.refAttachmentManager = refAttachmentManager

        // AttachmentManager autoAddToMessage is enabled no need to add manually
        // this.onAddToMessage(attachment)
    }

    const onRemoveAttachment = (attachment: any) => {
        props.triggerSave?.()
    }

    return (
        <>
            <PageTitle className    = "w-100-percent"
                       app          = {props.app}
                       pageId       = {props.pageId}
                       attachments  = {attachmentsCollection}/>

            <RichTextEditorUniversal key                        = {props.uniqKey}
                                     ref                        = {refTextarea}
                                     className                  = "gd-textarea-rte page-content-editor size-normal"
                                     showSettingsButton         = {false}
                                     hideControlsAllowed        = {false}
                                     showControls               = {false}
                                     showFloatingControls       = {!showControlsPanel}
                                     useLocalSettings           = {false}
                                     // localSettings              = {props.app.editorLocalSettings}
                                     attachmentsCollection      = {attachmentsCollection}
                                     placeholder                = "Type text here..."
                                     editable                   = {canEdit}
                                     focusOnMount               = {false}
                                     projectId                  = {props.app.projectId}
                                     mentions                   = {MentionsManager.getMentions({projectId: props.app.projectId})}
                                     stateSaveInterval          = {0}
                                     messageText                = {content}
                                     messageRTF                 = {contentRTF}
                                     onChange                   = {onMessageChange}
                                     onPasteImage               = {pasteImage}
                                     onAddAttachment            = {onAttachmentSelect}/>

            {showAttachments &&
                <AttachmentsManager ref                     = {refAttachmentManager}
                                    attachmentsCollection   = {attachmentsCollection}
                                    mode                    = {attachmentsManagerMode}
                                    dropzoneDOMElementId    = "ui-page"
                                    autoAddToMessage        = {true}
                                    saveNewUpload           = {onNewAttachment}
                                    saveRemoveFile          = {onRemoveAttachment}
                                    onAddToMessage          = {onAddToMessage}/>}
        </>
    )
}
